import { useMutation, useQueryClient, type MutateOptions, type QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { useState } from 'react';
import { client } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';
import { CallAPIError, callAPIProblem, readCallState, readCallCommand, snapshotCallStart, type NativeCall, type StartCallSnapshot } from './call-contract';
import { captureCallSession, currentCallSession, isCurrentCallSession, callAuthorization, callUUID, ownsCallKey, callKey, type CallSession } from './call-session';
import { readOwnedActiveCall, readOwnedCallToken, applyOwnedActiveCall } from './call-reads';
import { useCallStore } from './call-store';
import { isIncomingFor } from './call-wire';

type Operation = 'start' | 'answer' | 'reject' | 'end';
type Input = { operation: Operation; id?: string; snapshot?: StartCallSnapshot };
interface CallAction { owner: CallSession; id: number; input: Input; phase: 'pending' | 'uncertain' | 'reviewing' | 'reviewed'; observed?: NativeCall | null; failed?: boolean }
export const useCallActionState = create<{ action: CallAction | null; endFailure: CallAPIError | null }>(() => ({ action: null, endFailure: null }));
useAuthStore.subscribe((next, previous) => {
  if (sessionScope(next) !== sessionScope(previous)) useCallActionState.setState({ action: null, endFailure: null });
});
let sequence = 0;
const credentials = new WeakMap<object, string>();
function lease(action: CallAction) {
  currentCallSession(action.owner);
  if (useCallActionState.getState().action?.id !== action.id) throw new DOMException('Aborted', 'AbortError');
}
function invalidate(qc: QueryClient, owner: CallSession) {
  currentCallSession(owner);
  void qc.invalidateQueries({ predicate: query => ownsCallKey(query.queryKey, owner) });
}
function useCallWrite<V>(prepare: (value: V, owner: CallSession) => Input) {
  const [viewOwner] = useState(captureCallSession);
  const qc = useQueryClient();
  const mutation = useMutation({ mutationKey: ['calls', 'native-write'], retry: false, gcTime: 0,
    mutationFn: async (action: CallAction): Promise<NativeCall | undefined> => {
      lease(action);
      const authorization = credentials.get(action);
      if (!authorization) throw new CallAPIError('unauthorized');
      const { input, owner } = action;
      let response;
      try { response = await client.post<unknown>({ url: input.operation === 'start' ? '/calls/start' : '/calls/{id}/' + input.operation,
        ...(input.id ? { path: { id: input.id } } : {}), ...(input.snapshot ? { body: input.snapshot.body } : {}),
        headers: { Authorization: authorization, 'Accept-Language': owner.language } }); }
      catch { lease(action); throw new CallAPIError('unconfirmed', true); }
      lease(action);
      if (response.error || response.response?.status !== (input.operation === 'start' ? 201 : 200)) throw callAPIProblem(response.error, response.response?.status ?? 0, input.operation);
      if (input.operation === 'start' || input.operation === 'answer') return readCallState(response.data, owner, input.operation, input.id, input.snapshot)!;
      readCallCommand(response.data, owner, input.operation, input.id!);
    } });
  const mutateAsync = async (value: V): Promise<NativeCall | undefined> => {
    currentCallSession(viewOwner);
    const owner = captureCallSession(); currentCallSession(owner);
    const input = prepare(value, owner);
    if (useCallActionState.getState().action) throw new CallAPIError('pending');
    const authorization = callAuthorization(owner);
    const action: CallAction = { owner, input, id: ++sequence, phase: 'pending' };
    credentials.set(action, authorization); useCallActionState.setState({ action, endFailure: null });
    try {
      const call = await mutation.mutateAsync(action); lease(action);
      if (call) useCallStore.getState().setActive(call);
      else useCallStore.getState().ended(input.id!);
      useCallActionState.setState({ action: null }); invalidate(qc, owner);
      return call;
    } catch (error) {
      if (isCurrentCallSession(owner) && useCallActionState.getState().action?.id === action.id) {
        const definite = error instanceof CallAPIError && !error.uncertain;
        useCallActionState.setState({ action: definite ? null : { ...action, phase: 'uncertain' }, endFailure: definite && input.operation === 'end' ? error : null });
      }
      throw error;
    } finally { credentials.delete(action); }
  };
  const mutate = (value: V, options?: MutateOptions<NativeCall | undefined, unknown, V>) => {
    if (!isCurrentCallSession(viewOwner)) return;
    const owner = captureCallSession(), context = { client: qc, mutationKey: ['calls', 'native-write'], meta: undefined };
    void mutateAsync(value).then(result => { if (isCurrentCallSession(owner)) { options?.onSuccess?.(result, value, undefined, context); options?.onSettled?.(result, null, value, undefined, context); } },
      error => { if (isCurrentCallSession(owner)) { options?.onError?.(error, value, undefined, context); options?.onSettled?.(undefined, error, value, undefined, context); } });
  };
  return { ...mutation, mutate, mutateAsync };
}
export type StartCallArgs = { calleeId: string; type: 'voice' | 'video' } | { calleeIds: string[]; type: 'voice' | 'video' };
export function useStartCall() { return useCallWrite((value: StartCallArgs, owner) => ({ operation: 'start', snapshot: snapshotCallStart(value, owner) })); }
function idInput(operation: Operation, id: string): Input {
  if (!callUUID(id)) throw new CallAPIError('bad_input');
  return { operation, id };
}
export function useAnswerCall() { return useCallWrite((id: string) => idInput('answer', id)); }
export function useRejectCall() { return useCallWrite((id: string) => idInput('reject', id)); }
export function useEndCall() { return useCallWrite((id: string) => idInput('end', id)); }

export function useCallToken() {
  const [viewOwner] = useState(captureCallSession);
  const qc = useQueryClient();
  const mutation = useMutation({ retry: false, gcTime: 0, mutationKey: ['calls', 'native-token'], mutationFn: async ({ owner, id }: { owner: CallSession; id: string }) => readOwnedCallToken(owner, id) });
  const mutateAsync = async (id: string) => { currentCallSession(viewOwner); return mutation.mutateAsync({ owner: captureCallSession(), id }); };
  const mutate = (id: string, options?: MutateOptions<Awaited<ReturnType<typeof readOwnedCallToken>>, unknown, string>) => {
    if (!isCurrentCallSession(viewOwner)) return;
    const owner = captureCallSession();
    const context = { client: qc, mutationKey: ['calls', 'native-token'], meta: undefined };
    void mutation.mutateAsync({ owner, id }).then(result => { if (isCurrentCallSession(owner)) { options?.onSuccess?.(result, id, undefined, context); options?.onSettled?.(result, null, id, undefined, context); } },
      error => { if (isCurrentCallSession(owner)) { options?.onError?.(error, id, undefined, context); options?.onSettled?.(undefined, error, id, undefined, context); } });
  };
  return { ...mutation, mutate, mutateAsync };
}
export async function reviewCallAction(qc: QueryClient) {
  const action = useCallActionState.getState().action;
  if (!action || !['uncertain', 'reviewed'].includes(action.phase)) throw new CallAPIError('pending');
  lease(action); useCallActionState.setState({ action: { ...action, phase: 'reviewing', failed: false } });
  try {
    const owner = captureCallSession();
    await qc.cancelQueries({ queryKey: callKey('active', owner) }); lease(action);
    const revision = useCallStore.getState().revision;
    const call = await readOwnedActiveCall(owner); lease(action);
    applyOwnedActiveCall(call, owner, revision); qc.setQueryData(callKey('active', owner), call ? [call] : []);
    useCallActionState.setState({ action: { ...action, phase: 'reviewed', observed: call } });
  } catch (error) { lease(action); useCallActionState.setState({ action: { ...action, phase: 'uncertain', failed: true } }); throw error; }
}
/** A read reports current state only. Acceptance unlocks deliberate new actions;
 * it never replays a POST or claims which operation caused that state. */
export function acceptCallReview() {
  const action = useCallActionState.getState().action;
  if (action?.phase !== 'reviewed') throw new CallAPIError('pending');
  lease(action); useCallActionState.setState({ action: null });
}
export async function joinReviewedCall(qc: QueryClient) {
  const action = useCallActionState.getState().action;
  if (action?.phase !== 'reviewed' || !action.observed) throw new CallAPIError('pending');
  lease(action); useCallActionState.setState({ action: { ...action, phase: 'reviewing' } });
  try {
    const owner = captureCallSession(), call = await readOwnedActiveCall(owner); lease(action);
    if (!call || call.id !== action.observed.id || isIncomingFor(call, owner.user)) throw new CallAPIError('ended');
    // An explicit verified rejoin can revisit a locally dismissed room; passive
    // snapshots must continue respecting closedIds.
    useCallStore.setState(state => ({ closedIds: state.closedIds.filter(id => id !== call.id) }));
    useCallStore.getState().setActive(call);
    qc.setQueryData(callKey('active', owner), [call]);
    useCallActionState.setState({ action: null });
  } catch (error) { lease(action); useCallActionState.setState({ action: { ...action, phase: 'uncertain', failed: true } }); throw error; }
}
